
import { useState } from "react";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { DialogActions, DialogTitle } from "@mui/material";
import { styled } from '@mui/material/styles';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const MorePhotos: React.FunctionComponent<{ listImages: any[], onImagesAdded: (images: any[]) => void }> = (props) => {

    const [isOpenPhotosListing, setIsOpenPhotosListing] = useState(false);
    const [imagesSelected, setImagesSelected] = useState<any[]>([]);
    const [messageError, setMessageError] = useState<string>('');
    const MAX_IMAGES = 6;

    function getRandomSixDigitNumber(): number {
        return Math.floor(100000 + Math.random() * 900000);
    }

    // Compress and resize image for PDF compatibility
    const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<string> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const img = new window.Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Resize if larger than maxWidth
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, width, height);
                        // Convert to JPEG for better compression and PDF compatibility
                        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                        resolve(compressedBase64);
                    } else {
                        resolve(e.target?.result as string);
                    }
                };
                img.onerror = () => {
                    resolve(e.target?.result as string);
                };
                img.src = e.target?.result as string;
            };
        });
    };

    const handleSelectMultipleImages = (image: any) => {
        setImagesSelected((prev: any[]) => {
            const isAlreadySelected = prev.some((selectedImage) => selectedImage.id === image.id);

            if (isAlreadySelected) {
                return prev.filter((selectedImage) => selectedImage.id !== image.id);
            } else {
                if (prev.length < MAX_IMAGES) {
                    const selectedImageObj = {
                        id: image.id,
                        src: image.src,
                        ext: image.ext
                    };
                    return [...prev, selectedImageObj];
                }
                return prev;
            }
        });
    };

    const HandleChangeUpload = async (event: any) => {
        const files = Array.from(event.target.files);

        // Si l'utilisateur a sélectionné moins de 6 fichiers, afficher un avertissement
        if (files.length < MAX_IMAGES) {
            setMessageError(`Please select exactly ${MAX_IMAGES} images. You selected ${files.length} images.`);
            return;
        }

        // Prendre seulement les 6 premiers fichiers si plus de 6 sont sélectionnés
        const limitedFiles = files.slice(0, MAX_IMAGES);
        const updatedImages: any[] = [];
        setIsOpenPhotosListing(false);

        // Compress images for PDF compatibility
        const fileProcessors = limitedFiles.map(async (file: any) => {
            const compressedBase64 = await compressImage(file);
            const image = {
                id: getRandomSixDigitNumber(),
                src: compressedBase64,
                ext: 'jpeg', // Always JPEG after compression
            };
            updatedImages.push(image);
        });

        await Promise.all(fileProcessors);
        setImagesSelected(updatedImages);
        props.onImagesAdded(updatedImages);
    }


    return (
        <>
            <button
                className={`button-dash padd-20 back-done color-4F52C1 back-trans`}
                onClick={() => {
                    setIsOpenPhotosListing(true);
                    setImagesSelected([]);
                }}
            >
               Add More Photos...
            </button>


            <BootstrapDialog
                open={isOpenPhotosListing}
                onClose={() => setIsOpenPhotosListing(false)}
                aria-labelledby="customized-dialog-title"
                maxWidth="sm"
                fullWidth={true}
            >
                <DialogTitle >

                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setIsOpenPhotosListing(false)}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                   { messageError && <div className="title-18 hero-title">
                        <i className="fa-solid fa-circle-exclamation marg-right-10" style={{ color: '#eb4799' }}></i>
                        {messageError}
                    </div>}
                    <div className="d-flex justify-content-end mb-4">

                        <button
                            className={`button-dash back-746AFF color-4F52C1 border-trans padd-10 ${imagesSelected.length >= 1 ? 'disabled' : ''}`}
                        >
                            <label>
                                <p className="title-onest-18 bold-600 color-FFFFFF" style={{ margin: 0 }}>Upload photos 
                                </p>
                                <input
                                    className="form__file"
                                    id="upload-files-dialog"
                                    accept="image/*"
                                    type="file"
                                    multiple={true}
                                    onChange={HandleChangeUpload}
                                    hidden
                                    disabled={imagesSelected.length >= MAX_IMAGES}
                                />
                            </label>
                        </button>
                    </div>

                    {props.listImages == null ? (
                        <div></div>
                    ) : (
                        <div className="image-grid">
                            {props.listImages.map((image: any, index: number) => {
                                const isSelected = imagesSelected.some((selected) => selected.id === image.id);
                                const canSelect = !isSelected && imagesSelected.length < MAX_IMAGES;

                                return (
                                    <div
                                        key={image.id + "_" + index}
                                        className="select-image-container"
                                        onClick={() => canSelect || isSelected ? handleSelectMultipleImages(image) : null}
                                        style={{
                                            cursor: canSelect || isSelected ? "pointer" : "not-allowed",
                                            border: isSelected ? "3px solid #4F52C1" : "1px solid #ddd",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            opacity: canSelect || isSelected ? 1 : 0.5
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={image.id + "_" + index}
                                            value={image.src}
                                            checked={isSelected}
                                            onChange={() => canSelect || isSelected ? handleSelectMultipleImages(image) : null}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={!canSelect && !isSelected}
                                        />
                                        <img
                                            src={image.src}
                                            className="image"
                                            alt={`uploaded-${index}`}
                                        />

                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {imagesSelected.length !== MAX_IMAGES && (
                        <small className="text-danger">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {MAX_IMAGES - imagesSelected.length} more photos required
                        </small>
                    )}
                    {imagesSelected.length === MAX_IMAGES && (
                        <small className="text-success">
                            <i className="fa fa-check-circle me-1"></i>
                            All {MAX_IMAGES} photos selected ✓
                        </small>
                    )}
                </DialogContent>
                <DialogActions>
                    <div className="d-flex justify-content-between w-100 padd-10">
                        <div className="d-flex align-items-center">


                        </div>
                        <button
                            type="button"
                            className={`button-dash padd-20 ${imagesSelected.length === MAX_IMAGES
                                ? 'back-done color-4F52C1 back-trans'
                                : 'back-secondary color-gray disabled'
                                }`}
                            onClick={() => {
                                if (imagesSelected.length === MAX_IMAGES) {
                                    props.onImagesAdded(imagesSelected);
                                    setIsOpenPhotosListing(false);
                                }
                            }}
                            disabled={imagesSelected.length !== MAX_IMAGES}
                            style={{
                                opacity: imagesSelected.length === MAX_IMAGES ? 1 : 0.6,
                                cursor: imagesSelected.length === MAX_IMAGES ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {imagesSelected.length === MAX_IMAGES ? 'Save Photos' : `Need ${MAX_IMAGES - imagesSelected.length} more photos`}
                        </button>
                    </div>
                </DialogActions>
            </BootstrapDialog>
        </>
    )
}

export default MorePhotos;